import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Grid,
  Heading,
  Text,
  Badge,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Icon,
  useToast,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Divider,
} from '@chakra-ui/react';
import {
  MdCheckCircle,
  MdError,
  MdWarning,
  MdPlayArrow,
  MdRefresh,
  MdStorage,
  MdPeople,
  MdAttachMoney,
} from 'react-icons/md';
import systemAdminService from '../../services/system-admin.service';

const SystemAdministration = () => {
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [systemHealth, setSystemHealth] = useState(null);
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [payrollVerification, setPayrollVerification] = useState(null);
  const toast = useToast();

  const bgCard = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const successColor = useColorModeValue('green.500', 'green.300');
  const errorColor = useColorModeValue('red.500', 'red.300');
  const warningColor = useColorModeValue('orange.500', 'orange.300');

  useEffect(() => {
    loadSystemData();
  }, []);

  const loadSystemData = async () => {
    setLoading(true);
    try {
      const [healthRes, migrationRes, payrollRes] = await Promise.all([
        systemAdminService.getSystemHealth(),
        systemAdminService.getMigrationStatus(),
        systemAdminService.verifyPayrollSystemTables(),
      ]);

      if (healthRes.data?.payload?.data) {
        setSystemHealth(healthRes.data.payload.data);
      }
      if (migrationRes.data?.payload?.data) {
        setMigrationStatus(migrationRes.data.payload.data);
      }
      if (payrollRes.data?.payload?.data) {
        setPayrollVerification(payrollRes.data.payload.data);
      }
    } catch (error) {
      console.error('Error cargando datos del sistema:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar el estado del sistema',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteMigration = async (fileName) => {
    if (!window.confirm(`¿Está seguro de ejecutar la migración "${fileName}"?`)) {
      return;
    }

    setExecuting(true);
    try {
      const response = await systemAdminService.executeMigration(fileName);
      
      if (response.data?.status === 200) {
        toast({
          title: 'Migración ejecutada',
          description: `La migración ${fileName} se ejecutó correctamente`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        
        // Recargar datos
        await loadSystemData();
      } else {
        throw new Error(response.data?.message || 'Error desconocido');
      }
    } catch (error) {
      console.error('Error ejecutando migración:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'No se pudo ejecutar la migración',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setExecuting(false);
    }
  };

  if (loading) return null;

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Grid
        templateColumns={{ base: '1fr', lg: 'repeat(3, 1fr)' }}
        gap={6}
        mb={6}
      >
        {/* Salud de la Base de Datos */}
        <Card bg={bgCard} borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <Flex align="center" mb={4}>
              <Icon
                as={MdStorage}
                w={8}
                h={8}
                color={systemHealth?.database ? successColor : errorColor}
                mr={3}
              />
              <Box>
                <Text fontSize="sm" color="gray.500">
                  Base de Datos
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {systemHealth?.database ? 'Conectada' : 'Desconectada'}
                </Text>
              </Box>
            </Flex>
            <Badge
              colorScheme={systemHealth?.database ? 'green' : 'red'}
              fontSize="sm"
            >
              {systemHealth?.database ? 'Online' : 'Offline'}
            </Badge>
          </CardBody>
        </Card>

        {/* Estado de Migraciones */}
        <Card bg={bgCard} borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <Flex align="center" mb={4}>
              <Icon
                as={migrationStatus?.pending?.length > 0 ? MdWarning : MdCheckCircle}
                w={8}
                h={8}
                color={migrationStatus?.pending?.length > 0 ? warningColor : successColor}
                mr={3}
              />
              <Box>
                <Text fontSize="sm" color="gray.500">
                  Migraciones
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {migrationStatus?.pending?.length || 0} Pendientes
                </Text>
              </Box>
            </Flex>
            <Text fontSize="sm" color="gray.600">
              {migrationStatus?.executed?.length || 0} ejecutadas de{' '}
              {migrationStatus?.available?.length || 0} totales
            </Text>
          </CardBody>
        </Card>

        {/* Sistema de Liquidaciones */}
        <Card bg={bgCard} borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <Flex align="center" mb={4}>
              <Icon
                as={MdAttachMoney}
                w={8}
                h={8}
                color={payrollVerification?.allTablesExist ? successColor : warningColor}
                mr={3}
              />
              <Box>
                <Text fontSize="sm" color="gray.500">
                  Sistema de Liquidaciones
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {payrollVerification?.allTablesExist ? 'Activo' : 'Incompleto'}
                </Text>
              </Box>
            </Flex>
            <Text fontSize="sm" color="gray.600">
              {payrollVerification?.tables?.filter(t => t.exists).length || 0} de{' '}
              {payrollVerification?.tables?.length || 0} tablas
            </Text>
          </CardBody>
        </Card>
      </Grid>

      {/* Alertas */}
      {migrationStatus?.pending?.length > 0 && (
        <Alert status="warning" mb={6} borderRadius="lg">
          <AlertIcon />
          <Box flex="1">
            <AlertTitle>Migraciones pendientes</AlertTitle>
            <AlertDescription>
              Hay {migrationStatus.pending.length} migración(es) pendiente(s) de ejecutar.
              El sistema de liquidaciones requiere la migración 007.
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {!payrollVerification?.allTablesExist && (
        <Alert status="error" mb={6} borderRadius="lg">
          <AlertIcon />
          <Box flex="1">
            <AlertTitle>Sistema de liquidaciones incompleto</AlertTitle>
            <AlertDescription>
              Faltan las siguientes tablas: {payrollVerification?.missingTables?.join(', ')}
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {/* Migraciones Pendientes */}
      {migrationStatus?.pending?.length > 0 && (
        <Card bg={bgCard} borderWidth="1px" borderColor={borderColor} mb={6}>
          <CardHeader>
            <Flex justify="space-between" align="center">
              <Heading size="md">Migraciones Pendientes</Heading>
              <Button
                leftIcon={<MdRefresh />}
                size="sm"
                onClick={loadSystemData}
              >
                Actualizar
              </Button>
            </Flex>
          </CardHeader>
          <CardBody>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Archivo</Th>
                  <Th>Descripción</Th>
                  <Th>Acción</Th>
                </Tr>
              </Thead>
              <Tbody>
                {migrationStatus.pending.map((migration) => (
                  <Tr key={migration.id}>
                    <Td fontFamily="monospace" fontSize="sm">
                      {migration.fileName}
                    </Td>
                    <Td>{migration.description}</Td>
                    <Td>
                      <Button
                        leftIcon={<MdPlayArrow />}
                        size="sm"
                        colorScheme="blue"
                        onClick={() => handleExecuteMigration(migration.fileName)}
                      >
                        Ejecutar
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </CardBody>
        </Card>
      )}

      {/* Estado de Tablas del Sistema de Liquidaciones */}
      {payrollVerification && (
        <Card bg={bgCard} borderWidth="1px" borderColor={borderColor}>
          <CardHeader>
            <Heading size="md">Tablas del Sistema de Liquidaciones</Heading>
          </CardHeader>
          <CardBody>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Tabla</Th>
                  <Th>Estado</Th>
                </Tr>
              </Thead>
              <Tbody>
                {payrollVerification.tables?.map((table) => (
                  <Tr key={table.name}>
                    <Td fontFamily="monospace">{table.name}</Td>
                    <Td>
                      <Flex align="center">
                        <Icon
                          as={table.exists ? MdCheckCircle : MdError}
                          color={table.exists ? successColor : errorColor}
                          mr={2}
                        />
                        <Text color={table.exists ? successColor : errorColor}>
                          {table.exists ? 'Existe' : 'No existe'}
                        </Text>
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </CardBody>
        </Card>
      )}
    </Box>
  );
};

export default SystemAdministration;
